"use client";

import { BaseTxButton, Step } from "logic/components";
import { cn } from "logic/utils";
import { type ReactNode } from "react";
import { Skeleton } from "ui/components";

import { usePendingBridgeToast } from "../../usePendingBridgeToast";
import { useBridgeAndRepay } from "../../useRepayTx/useBridgeAndRepay";

import { useLoanPayment } from "./LoanPaymentProvider";
import { QuoteUnavailableNote } from "./QuoteUnavailableNote";

interface LoanPaymentTxDetailsProps {
  onDone: () => void;
}

export function LoanPaymentTxDetails({ onDone }: LoanPaymentTxDetailsProps) {
  const { loan, loanTimestamps, isPrepayment, isQuoteUnavailable, customPaymentAmountUnits, customPaymentAmountError } =
    useLoanPayment();
  const { isRepayment } = loanTimestamps;

  // Scoped to prepayment like the isDisabled chain below: customPaymentAmount
  // survives switching back to the Monthly Payment tab, and a stale error there
  // must not suppress the monthly simulation, which never uses that amount.
  const isAmountValid = !isPrepayment || !customPaymentAmountError;

  const {
    action,
    steps,
    currentStepIndex,
    repayTx,
    bridgeTx,
    needsBridge,
    isBalanceInsufficient,
    isBridgeInFlight,
    isLoadingBalances,
  } = useBridgeAndRepay(loan, isPrepayment ? customPaymentAmountUnits : undefined, isAmountValid);

  usePendingBridgeToast();

  const currentStep = steps[currentStepIndex];
  const isDone = steps.at(-1)?.status == "confirmed";

  const isLoading = currentStep.status == "sending" || currentStep.status == "confirming";

  let isDisabled;
  if (isQuoteUnavailable) isDisabled = true;
  else if (isPrepayment) isDisabled = loanTimestamps.isGracePeriod || Boolean(customPaymentAmountError);
  else if (!isRepayment) isDisabled = true;
  else isDisabled = false;
  isDisabled = isDisabled || isBalanceInsufficient || isBridgeInFlight || isLoadingBalances;

  let buttonText: ReactNode = currentStep.action;
  if (isQuoteUnavailable) buttonText = "Payment Unavailable";
  else if (isDone) buttonText = "Done";
  else if (!isPrepayment && !isRepayment) buttonText = "Loan is Current";
  else if (isBalanceInsufficient) buttonText = "Insufficient Balance";
  else if (isBridgeInFlight) buttonText = "Waiting for Transfer...";

  return (
    <>
      <div className="flex flex-col grow px-8">
        {isQuoteUnavailable ? (
          <QuoteUnavailableNote />
        ) : isLoadingBalances ? (
          <StepsSkeleton />
        ) : needsBridge ? (
          <>
            <StepGroup label="" isLabelDisabled={false}>
              {bridgeTx.steps.map((step, index) => (
                <Step
                  key={`bridge-${index}`}
                  step={step}
                  index={index}
                  currentStepIndex={bridgeTx.isDone ? bridgeTx.steps.length : bridgeTx.currentStepIndex}
                />
              ))}
            </StepGroup>
            <StepGroup label="" isLabelDisabled={!bridgeTx.isDone}>
              {repayTx.steps.map((step, index) => (
                <Step
                  key={`repay-${index}`}
                  step={step}
                  index={index}
                  currentStepIndex={bridgeTx.isDone ? repayTx.currentStepIndex : -1}
                />
              ))}
            </StepGroup>
          </>
        ) : (
          repayTx.steps.map((step, index) => (
            <Step key={`repay-${index}`} step={step} index={index} currentStepIndex={repayTx.currentStepIndex} />
          ))
        )}
      </div>

      <BaseTxButton
        onClick={isDone ? onDone : action.send}
        disabled={isQuoteUnavailable || (isDone ? false : isDisabled || !action.send)}
        isLoading={isDone ? false : isLoading}
        isDone={isDone}
        onDone={onDone}
      >
        {buttonText}
      </BaseTxButton>
    </>
  );
}

function StepGroup({
  label,
  isLabelDisabled,
  children,
}: {
  label: string;
  isLabelDisabled: boolean;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col">
      <span className={cn("font-medium text-sm mb-1", { "text-text-dark-secondary": isLabelDisabled })}>{label}</span>
      {children}
    </div>
  );
}

function StepsSkeleton() {
  return (
    <div className="flex flex-col gap-4 my-4">
      {[0, 1].map((i) => (
        <div key={i} className="flex items-start gap-3">
          <Skeleton className="mt-1 h-4 w-4 shrink-0" />
          <Skeleton className="h-4 w-32" />
        </div>
      ))}
    </div>
  );
}
